import { useEffect, useMemo, useRef, useState } from 'react';
import { useMemoizedFn } from 'ahooks';
import { v4 as uuidv4 } from 'uuid';
import type { tMatchingQuery, tMatchingSelection } from '../Directory.matching';
import { tDirectoryLead } from '../Directory.types';
import {
  DirectoryOperationsService,
  parseDirectoryError,
} from './DirectoryOperations.service';
import {
  operationsStorageKey,
  tPendingWrite,
  tStoredOperations,
  updateStoredOperations,
} from './DirectoryOperations.storage';
import {
  TERMINAL_OPERATION_STATES,
  tBulkOperation,
  tCapturedSelection,
  tDirectoryApiError,
  tOperationAction,
  tOperationKind,
  tOperationPreview,
  tPreviewWarning,
} from './DirectoryOperations.types';

/**
 * Directory v2, U8: the priority trio (archive, move, assign) on the
 * directory.v1 contract. Trigger and poll only; the Directory mounts it
 * through `useDirectoryOperationsWithRecovery`, which adds recovery.
 *
 * - Dialog: move and assign run on their own button: capture + preview,
 *   then the operation starts at once unless the preview has something the
 *   user must see first (`reviewReasons`: archived picks, auto-assignment,
 *   blocked leads, nothing to change, 1,000+ leads). Archive, which has no
 *   parameters, always asks once. Every write carries an Idempotency-Key,
 *   and a retry after a lost answer reuses it, so the server returns the
 *   operation it already created instead of creating a second one.
 * - Tracked operations: polled with bounded backoff until terminal. A poll
 *   without an answer means "status unavailable", never "failed": the work
 *   runs on the server regardless (R11). A refusal (4xx other than 429)
 *   will not change on the next tick, so it stops that operation's polling.
 * - Undo: offered until the server's `undo_expires_at` (R9), one per
 *   operation (decision 6).
 * - Where directory.v1 does not admit an action (`policy_unavailable`), its
 *   previous dialog takes over with the same picks, and that action goes
 *   there directly until the workspace or actor changes. Per action: archive
 *   has a gate of its own (held capacity for its Undo).
 */

export type tOperationDialogStep = 'parameters' | 'previewing' | 'review' | 'confirming';

export type tOperationDialog = {
  /** Identifies this dialog: its fields reset when it changes. */
  id: string;
  kind: tOperationKind;
  /** The picks when the dialog opened (BUG-724: never the live selection). */
  leadIds: string[];
  /** Leads the action covers as the user chose them: the picks, or the whole match. */
  pickedCount: number;
  /** Every lead matching the view, captured on the server instead of by id. */
  matching?: { query: tMatchingQuery };
  archivedCount: number;
  step: tOperationDialogStep;
  action?: tOperationAction;
  /** Card title for the operation this dialog creates. */
  title?: string;
  selection?: tCapturedSelection;
  /** A caller already captured membership; expiry must never recapture a broader set. */
  captured?: boolean;
  preview?: tOperationPreview;
  error?: tDirectoryApiError;
  /** One capture per snapshot; replaying the key returns the same selection. */
  captureKey: string;
  /** One create per preview; a retry after a lost answer reuses it. */
  createKey?: string;
};

export type tTrackedOperation = {
  operation: tBulkOperation;
  title: string;
  /** No answer to the status poll for a while; the work may still be running. */
  statusUnavailable?: boolean;
  /** Polling stopped: no answer for too long, or the server refused (pollError). */
  pollStopped?: boolean;
  pollError?: tDirectoryApiError;
  undo?: {
    status: 'requesting' | 'requested' | 'failed';
    /** Idempotency-Key of the Undo request; a retry after a lost answer reuses it. */
    key: string;
    error?: tDirectoryApiError;
  };
};

/**
 * The first status read comes early: every chunk runs in a queue worker that
 * starts it at once, so a small operation is usually done by then.
 */
const FIRST_POLL_MS = 500;
const FAST_POLL_MS = 1_500;
const SLOW_POLL_MS = 5_000;
const FAST_POLL_WINDOW_MS = 30_000;
/** Without an answer for this long, the card says the status is unavailable. */
const STATUS_UNAVAILABLE_AFTER_MS = 15_000;
/** Without an answer for this long, polling stops; the card keeps saying so. */
const POLL_GIVE_UP_MS = 10 * 60_000;

/** Refusals a fresh capture fixes: the snapshot expired, changed or is gone. */
const RECAPTURE_REASONS: ReadonlySet<tDirectoryApiError['reason']> = new Set([
  'selection_expired',
  'selection_revision_changed',
  'resource_not_found',
]);

/** Refusals at confirm that a fresh preview fixes. */
const REPREVIEW_REASONS: ReadonlySet<tDirectoryApiError['reason']> = new Set([
  'preview_expired',
  'preview_stale',
]);

/** Refusals that clear by themselves: trying again later can succeed. */
const WAIT_REASONS: ReadonlySet<tDirectoryApiError['reason']> = new Set([
  'rate_limited',
  'operation_conflict',
]);

/** Decision 4: from this many leads, the user types the number to confirm. */
export const TYPED_CONFIRMATION_FROM = 1000;

export type tReviewReason =
  | 'blocked'
  | 'nothingToChange'
  | 'autoAssign'
  | 'assigneeAccess'
  | 'archived'
  | 'large';

type tAssigneeAccessWarning = Extract<tPreviewWarning, { reason: 'assignee_without_funnel_access' }>;

/**
 * The warning of an assignment whose new owner cannot see the funnel some of
 * the leads are in (BUG-1031). The leads are assigned anyway: the dialog stops
 * to say who cannot see what, and the person assigning decides.
 */
export function assigneeAccessWarning(
  preview: tOperationPreview,
): tAssigneeAccessWarning | undefined {
  return preview.warnings.find(
    (warning): warning is tAssigneeAccessWarning =>
      warning.reason === 'assignee_without_funnel_access' &&
      warning.affected_count > 0 &&
      warning.funnels.length > 0,
  );
}

/** What the dialog can say about blocked leads, each with how many it covers. */
export type tBlockedLine = {
  why: 'ownerWithoutDestinationAccess' | 'permissionChanged' | 'tagLimitExceeded' | 'unexplained';
  count: number;
};

/**
 * The blocked leads of a preview by what can be said about them.
 *
 * - On a move, `invalid_assignee` is the owner a lead keeps, who cannot see the
 *   destination funnel.
 * - `permission_changed` is a lead the person can no longer change, or that is gone.
 * - Anything else, and every blocked lead of a revision 1 answer (no
 *   `blocked_reasons`), stays unexplained: a count, never a guessed reason.
 */
export function blockedBreakdown(kind: tOperationKind, preview: tOperationPreview): tBlockedLine[] {
  const reasons = preview.blocked_reasons ?? {};
  const lines: tBlockedLine[] = [];
  const owner = kind === 'move' ? (reasons.invalid_assignee ?? 0) : 0;
  if (owner > 0) lines.push({ why: 'ownerWithoutDestinationAccess', count: owner });
  const permission = reasons.permission_changed ?? 0;
  if (permission > 0) lines.push({ why: 'permissionChanged', count: permission });
  const tagLimit = kind === 'tag' ? (reasons.tag_limit_exceeded ?? 0) : 0;
  if (tagLimit > 0) lines.push({ why: 'tagLimitExceeded', count: tagLimit });
  const unexplained = preview.blocked_count - owner - permission - tagLimit;
  if (unexplained > 0) lines.push({ why: 'unexplained', count: unexplained });
  return lines;
}

/**
 * What the user must see before an operation runs. Empty means nothing worth
 * a stop, so a move or an assignment starts on its own button. Leads already
 * in the target state are not a reason: the card counts them afterwards.
 */
export function reviewReasons(
  dialog: Pick<tOperationDialog, 'kind' | 'archivedCount'>,
  preview: tOperationPreview,
): tReviewReason[] {
  const reasons: tReviewReason[] = [];
  if (!preview.confirmation_allowed || preview.blocked_count > 0) reasons.push('blocked');
  // "Nothing to change" is true only when every lead already is as asked. With
  // blocked leads it was false, and it was what the dialog said (BUG-1031).
  if (preview.eligible_count === 0 && preview.blocked_count === 0) reasons.push('nothingToChange');
  if (
    preview.warnings.some(
      (warning) => warning.reason === 'automatic_assignment' && warning.affected_count > 0,
    )
  ) {
    reasons.push('autoAssign');
  }
  if (assigneeAccessWarning(preview)) reasons.push('assigneeAccess');
  // A move keeps archived leads archived: worth saying before it runs.
  if (dialog.kind === 'move' && dialog.archivedCount > 0) reasons.push('archived');
  if (preview.selected_count >= TYPED_CONFIRMATION_FROM) reasons.push('large');
  return reasons;
}

export function isTerminal(operation: tBulkOperation): boolean {
  return TERMINAL_OPERATION_STATES.has(operation.state);
}

/** Undo is offered while the server's deadline has not passed (R9). */
export function undoOpen(operation: tBulkOperation, now: number): boolean {
  if (operation.action === 'undo' || !operation.undo_expires_at) return false;
  if (!isTerminal(operation) || operation.state === 'failed') return false;
  return new Date(operation.undo_expires_at).getTime() > now;
}

/**
 * No answer, or a gateway error without a contract reason: the write may have
 * happened. Anything else is the server's decision, and nothing was created.
 */
export function outcomeUnknown(error: tDirectoryApiError): boolean {
  return error.reason === 'network' || (error.reason === 'unknown' && error.status >= 500);
}

/** Whether a button that repeats the failed step can succeed. */
export function canRetry(error: tDirectoryApiError): boolean {
  return (
    error.retryable ||
    outcomeUnknown(error) ||
    WAIT_REASONS.has(error.reason) ||
    RECAPTURE_REASONS.has(error.reason) ||
    REPREVIEW_REASONS.has(error.reason)
  );
}

/** A 4xx other than 429 answers the same on every poll. */
function refusesPolling(error: tDirectoryApiError): boolean {
  return error.status >= 400 && error.status < 500 && error.reason !== 'rate_limited';
}

/** A write whose outcome is unknown is shown as a network loss. */
function writeError(error: tDirectoryApiError): tDirectoryApiError {
  return outcomeUnknown(error) ? { ...error, reason: 'network' } : error;
}

export type tOpenOperationRequest =
  | { kind: tOperationKind; leads: Pick<tDirectoryLead, 'id' | 'active'>[] }
  | { kind: tOperationKind; matching: tMatchingSelection }
  | { kind: tOperationKind; selection: tCapturedSelection };

export type tDirectoryOperationsOptions = {
  workspaceId: string | undefined;
  actorId: string | undefined;
  enabled: boolean;
  /** Generic titles for operations recovered without a stored one. */
  defaultTitle: (kind: tBulkOperation['action']) => string;
  undoTitle: (title: string) => string;
  /** A tracked operation finished: refresh the rows and counts. */
  onOperationFinished: () => void;
  /** The picks were spent by an accepted operation. */
  onOperationAccepted: () => void;
  /** The pre-v1 dialog for this action, with these picks. */
  openLegacyDialog: (request: { kind: tOperationKind; leadIds: string[] }) => void;
  allowLegacyFallback?: boolean;
};

export function useDirectoryOperations(options: tDirectoryOperationsOptions) {
  const { workspaceId, actorId } = options;
  const identity = JSON.stringify([workspaceId, actorId]);
  const identityRef = useRef({ identity, epoch: 0 });
  if (identityRef.current.identity !== identity) {
    identityRef.current = { identity, epoch: identityRef.current.epoch + 1 };
  }
  useEffect(() => () => { identityRef.current.epoch += 1; }, []);
  const [dialog, setDialog] = useState<tOperationDialog | null>(null);
  const [operations, setOperations] = useState<tTrackedOperation[]>([]);
  // Actions directory.v1 answered it does not admit for this workspace.
  const [closedKinds, setClosedKinds] = useState<ReadonlySet<tOperationKind>>(new Set());

  // Callbacks change identity every render; the timers read the latest.
  const optionsRef = useRef(options);
  optionsRef.current = options;
  // The dialog as last rendered: an auto-run checks it is still the same one.
  const dialogRef = useRef(dialog);
  dialogRef.current = dialog;
  const trackedAtRef = useRef<Record<string, number>>({});
  const lastAnswerRef = useRef<Record<string, number>>({});
  const pausedUntilRef = useRef(0);

  const storageKey = workspaceId && actorId ? operationsStorageKey(workspaceId, actorId) : null;

  function remember(update: (state: tStoredOperations) => tStoredOperations) {
    if (storageKey) updateStoredOperations(storageKey, update);
  }

  function rememberPending(write: tPendingWrite) {
    remember((state) => ({
      ...state,
      pending: [...state.pending.filter((entry) => entry.key !== write.key), write],
    }));
  }

  function forgetPending(key: string, created?: { id: string; title: string }) {
    remember((state) => ({
      titles: created
        ? { ...state.titles, [created.id]: { title: created.title, at: Date.now() } }
        : state.titles,
      pending: state.pending.filter((entry) => entry.key !== key),
    }));
  }

  function closeKind(kind: tOperationKind) {
    setClosedKinds((prev) => new Set(prev).add(kind));
  }

  function startClock(operationId: string) {
    const now = Date.now();
    trackedAtRef.current[operationId] ??= now;
    lastAnswerRef.current[operationId] ??= now;
  }

  function track(operation: tBulkOperation, title: string) {
    startClock(operation.id);
    setOperations((prev) => [
      { operation, title },
      ...prev.filter((tracked) => tracked.operation.id !== operation.id),
    ]);
  }

  function patchTracked(operationId: string, patch: Partial<tTrackedOperation>) {
    setOperations((prev) =>
      prev.map((tracked) =>
        tracked.operation.id === operationId ? { ...tracked, ...patch } : tracked,
      ),
    );
  }

  /** Seeds operations found on the server (recovery), after any already tracked. */
  function adoptOperations(recovered: tTrackedOperation[]) {
    recovered.forEach(({ operation }) => startClock(operation.id));
    setOperations((prev) => {
      const known = new Set(prev.map((tracked) => tracked.operation.id));
      return [...prev, ...recovered.filter(({ operation }) => !known.has(operation.id))];
    });
  }

  // ---- Dialog ---------------------------------------------------------------

  function openOperationDialog(request: tOpenOperationRequest) {
    if ('selection' in request) {
      setDialog({
        id: uuidv4(),
        kind: request.kind,
        leadIds: [],
        pickedCount: request.selection.selected_count,
        archivedCount: 0,
        step: 'parameters',
        captureKey: uuidv4(),
        selection: request.selection,
        captured: true,
      });
      return;
    }
    if ('matching' in request) {
      // The previous dialogs need lead ids, so a whole match never goes there.
      setDialog({
        id: uuidv4(),
        kind: request.kind,
        leadIds: [],
        pickedCount: request.matching.total,
        matching: { query: request.matching.query },
        archivedCount: request.matching.archivedCount ?? 0,
        step: 'parameters',
        captureKey: uuidv4(),
      });
      return;
    }
    if (request.kind !== 'tag' && closedKinds.has(request.kind) && optionsRef.current.allowLegacyFallback !== false) {
      optionsRef.current.openLegacyDialog({
        kind: request.kind,
        leadIds: request.leads.map((lead) => lead.id),
      });
      return;
    }
    setDialog({
      id: uuidv4(),
      kind: request.kind,
      leadIds: request.leads.map((lead) => lead.id),
      pickedCount: request.leads.length,
      archivedCount: request.leads.filter((lead) => !lead.active).length,
      step: 'parameters',
      captureKey: uuidv4(),
    });
  }

  function closeOperationDialog() {
    setDialog(null);
  }

  function switchToLegacyDialog() {
    if (!dialog || dialog.kind === 'tag' || dialog.matching || dialog.captured || optionsRef.current.allowLegacyFallback === false) return;
    setDialog(null);
    optionsRef.current.openLegacyDialog({ kind: dialog.kind, leadIds: dialog.leadIds });
  }

  /**
   * Captures (once per snapshot) and previews. Returns the dialog as it
   * stands once reviewed, without showing it: the caller decides whether the
   * user needs to see it. A failure is shown here, back on the parameters.
   */
  async function preparePreview(
    current: tOperationDialog,
    request: { action: tOperationAction; title: string },
  ): Promise<tOperationDialog | null> {
    if (!workspaceId) return null;
    const pending: tOperationDialog = {
      ...current,
      step: 'previewing',
      action: request.action,
      title: request.title,
      preview: undefined,
      createKey: undefined,
      error: undefined,
    };
    setDialog(pending);

    try {
      // One capture per snapshot: changing the destination previews the same
      // picks again instead of capturing them again.
      const selection =
        current.selection ??
        (current.matching
          ? await DirectoryOperationsService.captureMatching(workspaceId, {
              query: current.matching.query,
              idempotencyKey: current.captureKey,
            })
          : await DirectoryOperationsService.captureExplicit(workspaceId, {
              leadIds: current.leadIds,
              idempotencyKey: current.captureKey,
            }));
      setDialog((prev) => (prev?.id === current.id ? { ...prev, selection } : prev));

      const preview = await DirectoryOperationsService.preview(workspaceId, {
        selection: { id: selection.id, revision: selection.revision },
        action: request.action,
      });
      return { ...pending, selection, step: 'review', preview, createKey: uuidv4() };
    } catch (err) {
      const error = parseDirectoryError(err);
      if (error.reason === 'policy_unavailable') closeKind(current.kind);
      setDialog((prev) =>
        prev?.id === current.id
          ? {
              ...prev,
              step: 'parameters',
              error,
              // A snapshot that expired, changed or is gone is captured again,
              // under a new key: the old one would return the old snapshot.
              ...(RECAPTURE_REASONS.has(error.reason) && !current.captured && {
                selection: undefined,
                captureKey: uuidv4(),
              }),
            }
          : prev,
      );
      return null;
    }
  }

  function idle(current: tOperationDialog | null): current is tOperationDialog {
    return Boolean(current) && current!.step !== 'previewing' && current!.step !== 'confirming';
  }

  /** Previews and always shows the result (archive, and retries). */
  async function requestPreview(request: { action: tOperationAction; title: string }) {
    if (!idle(dialog)) return;
    const current = dialog;
    const reviewed = await preparePreview(current, request);
    if (reviewed) setDialog((prev) => (prev?.id === current.id ? reviewed : prev));
  }

  /**
   * Move and assign: the button that names the action runs it. The preview
   * still happens first; only when it has something to say does the dialog
   * stop on it (see `reviewReasons`).
   */
  async function submitOperation(request: { action: tOperationAction; title: string }) {
    if (!idle(dialog)) return;
    const current = dialog;
    const reviewed = await preparePreview(current, request);
    if (!reviewed || dialogRef.current?.id !== current.id) return;
    if (reviewReasons(reviewed, reviewed.preview!).length) {
      setDialog((prev) => (prev?.id === current.id ? reviewed : prev));
      return;
    }
    await createFrom(reviewed);
  }

  async function createFrom(current: tOperationDialog) {
    if (!workspaceId || !current.preview?.confirmation_allowed) return;
    const identityEpoch = identityRef.current.epoch;
    const previewId = current.preview.id;
    const title = current.title ?? optionsRef.current.defaultTitle(current.kind);
    const idempotencyKey = current.createKey ?? uuidv4();

    // Written before the request: if the answer is lost, recovery replays it.
    rememberPending({ kind: 'create', key: idempotencyKey, previewId, title, at: Date.now() });
    setDialog({ ...current, step: 'confirming', createKey: idempotencyKey, error: undefined });

    try {
      const operation = await DirectoryOperationsService.create(workspaceId, {
        previewId,
        idempotencyKey,
      });
      forgetPending(idempotencyKey, { id: operation.id, title });
      if (identityRef.current.epoch !== identityEpoch) return;
      track(operation, title);
      setDialog(null);
      optionsRef.current.onOperationAccepted();
      // The server runs the first chunk before answering: a small selection is already done.
      if (isTerminal(operation)) optionsRef.current.onOperationFinished();
    } catch (err) {
      const parsed = parseDirectoryError(err);
      const unknown = outcomeUnknown(parsed);
      // Only an unanswered request stays pending: any answer is final.
      if (!unknown) forgetPending(idempotencyKey);
      if (identityRef.current.epoch !== identityEpoch) return;
      const error = writeError(parsed);
      if (error.reason === 'policy_unavailable') closeKind(current.kind);
      const needsPreview = REPREVIEW_REASONS.has(error.reason);
      setDialog((prev) =>
        prev?.id === current.id
          ? {
              ...prev,
              step: needsPreview ? 'parameters' : 'review',
              error,
              createKey: unknown ? idempotencyKey : uuidv4(),
              ...(needsPreview && { preview: undefined }),
            }
          : prev,
      );
    }
  }

  /** The confirm button of a dialog stopped on its preview. */
  async function confirmOperation() {
    if (dialog?.step !== 'review') return;
    await createFrom(dialog);
  }

  // ---- Undo -----------------------------------------------------------------

  async function undoOperation(operationId: string) {
    if (!workspaceId) return;
    const identityEpoch = identityRef.current.epoch;
    const original = operations.find((tracked) => tracked.operation.id === operationId);
    if (!original || !undoOpen(original.operation, Date.now())) return;
    if (original.undo && original.undo.status !== 'failed') return;

    const retryKey =
      original.undo?.error && outcomeUnknown(original.undo.error) ? original.undo.key : undefined;
    const idempotencyKey = retryKey ?? uuidv4();
    const title = optionsRef.current.undoTitle(original.title);
    rememberPending({ kind: 'undo', key: idempotencyKey, operationId, title, at: Date.now() });
    patchTracked(operationId, { undo: { status: 'requesting', key: idempotencyKey } });

    try {
      const compensation = await DirectoryOperationsService.undo(workspaceId, {
        operationId,
        idempotencyKey,
      });
      forgetPending(idempotencyKey, { id: compensation.id, title });
      if (identityRef.current.epoch !== identityEpoch) return;
      patchTracked(operationId, { undo: { status: 'requested', key: idempotencyKey } });
      track(compensation, title);
      if (isTerminal(compensation)) optionsRef.current.onOperationFinished();
    } catch (err) {
      const parsed = parseDirectoryError(err);
      if (!outcomeUnknown(parsed)) forgetPending(idempotencyKey);
      if (identityRef.current.epoch !== identityEpoch) return;
      patchTracked(operationId, {
        undo: { status: 'failed', key: idempotencyKey, error: writeError(parsed) },
      });
    }
  }

  function dismissOperation(operationId: string) {
    setOperations((prev) => prev.filter((tracked) => tracked.operation.id !== operationId));
  }

  // ---- Polling ----------------------------------------------------------------

  const running = operations
    .filter((tracked) => !isTerminal(tracked.operation) && !tracked.pollStopped)
    .map((tracked) => tracked.operation.id);
  const runningKey = running.join(',');

  useEffect(() => {
    if (!workspaceId || !running.length) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function poll(operationId: string) {
      const silentFor = Date.now() - (lastAnswerRef.current[operationId] ?? Date.now());
      if (silentFor > POLL_GIVE_UP_MS) {
        patchTracked(operationId, { statusUnavailable: true, pollStopped: true });
        return;
      }

      try {
        const operation = await DirectoryOperationsService.get(workspaceId!, operationId);
        if (cancelled) return;
        lastAnswerRef.current[operationId] = Date.now();
        patchTracked(operationId, { operation, statusUnavailable: false });
        if (isTerminal(operation)) optionsRef.current.onOperationFinished();
      } catch (err) {
        if (cancelled) return;
        const error = parseDirectoryError(err);
        if (refusesPolling(error)) {
          patchTracked(operationId, { pollStopped: true, pollError: error });
        } else if (error.reason === 'rate_limited') {
          pausedUntilRef.current = Date.now() + (error.retryAfterSeconds ?? 5) * 1000;
        } else if (Date.now() - (lastAnswerRef.current[operationId] ?? 0) > STATUS_UNAVAILABLE_AFTER_MS) {
          // No answer (network, 5xx): keep polling, the work runs server-side.
          patchTracked(operationId, { statusUnavailable: true });
        }
      }
    }

    async function tick() {
      if (Date.now() >= pausedUntilRef.current) await Promise.all(running.map(poll));
      if (cancelled) return;

      const oldest = Math.min(...running.map((id) => trackedAtRef.current[id] ?? Date.now()));
      const delay = Date.now() - oldest < FAST_POLL_WINDOW_MS ? FAST_POLL_MS : SLOW_POLL_MS;
      timer = setTimeout(tick, delay);
    }

    timer = setTimeout(tick, FIRST_POLL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- BUG-1115 U8: preserve the existing trigger array (KTD4).
  }, [workspaceId, actorId, runningKey]);

  // A different workspace or actor never sees the previous one's cards.
  useEffect(() => {
    setOperations([]);
    setDialog(null);
    setClosedKinds(new Set());
    pausedUntilRef.current = 0;
  }, [workspaceId, actorId]);

  const stableOpenOperationDialog = useMemoizedFn(openOperationDialog);
  const stableCloseOperationDialog = useMemoizedFn(closeOperationDialog);
  const stableSwitchToLegacyDialog = useMemoizedFn(switchToLegacyDialog);
  const stableRequestPreview = useMemoizedFn(requestPreview);
  const stableSubmitOperation = useMemoizedFn(submitOperation);
  const stableConfirmOperation = useMemoizedFn(confirmOperation);
  const stableUndoOperation = useMemoizedFn(undoOperation);
  const stableDismissOperation = useMemoizedFn(dismissOperation);
  const stableAdoptOperations = useMemoizedFn(adoptOperations);

  return useMemo(() => ({
    dialog,
    operations,
    openOperationDialog: stableOpenOperationDialog,
    closeOperationDialog: stableCloseOperationDialog,
    switchToLegacyDialog: stableSwitchToLegacyDialog,
    requestPreview: stableRequestPreview,
    submitOperation: stableSubmitOperation,
    confirmOperation: stableConfirmOperation,
    undoOperation: stableUndoOperation,
    dismissOperation: stableDismissOperation,
    adoptOperations: stableAdoptOperations,
  }), [
    dialog, operations, stableOpenOperationDialog, stableCloseOperationDialog,
    stableSwitchToLegacyDialog, stableRequestPreview, stableSubmitOperation,
    stableConfirmOperation, stableUndoOperation, stableDismissOperation,
    stableAdoptOperations,
  ]);
}
